import {useMutation} from '@github-ui/react-query'
import type {Task, HydratedTask, CreateTaskParams, CreateJobParams, NewTaskPayload} from '../types/task'
import type {Session} from '../types/session'
import {useCAPIQuery} from './useCAPIQuery'
import {fetchCAPI} from '../utils/fetch-capi'
import {useState, useMemo} from 'react'
import {usePullRequests} from './usePullRequests'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {IdToPullRequestMap} from '../types/pull'

// temporary, for createTaskViaMonolith function parameters
type CreateTaskViaMonolithParams = CreateTaskParams & {
  isBranchSelectionEnabled: boolean
}

// Helper function to create job via the Jobs API. Should be removed with feature flag cca_create_session_from_front_end
async function createJob({
  id,
  nwo,
  problemStatement,
  baseRef,
  apiURL,
  isBranchSelectionEnabled,
  title,
}: CreateJobParams): Promise<void> {
  const endpoint = `/agents/swe/jobs/${nwo}`
  await fetchCAPI({
    apiURL,
    path: endpoint,
    method: 'POST',
    body: {
      problem_statement: problemStatement,
      ...(isBranchSelectionEnabled && {
        pull_request: {
          base_ref: `refs/heads/${baseRef}`,
          ...(title && {title}),
        },
      }),
      event_identifiers: [id],
    },
  })
}

// Helper function to create tasks via the monolith (fallback approach)
async function createTaskViaMonolith({
  id,
  nwo,
  problemStatement,
  baseRef,
  isBranchSelectionEnabled,
}: CreateTaskViaMonolithParams): Promise<void> {
  const endpoint = `/${nwo}/agent-sessions`
  await verifiedFetchJSON(endpoint, {
    method: 'POST',
    body: {
      problem_statement: problemStatement,
      ...(isBranchSelectionEnabled && {base_ref: baseRef}),
      creation_id: id,
    },
  })
}

function sortTasks(sessions: Session[]): Task[] {
  const resourceIdToTaskMap = new Map<string, Task>()

  for (const session of sessions) {
    const task = resourceIdToTaskMap.get(session.resource_id.toString())
    if (task) {
      task.session_count += 1
      if (new Date(session.last_updated_at) > new Date(task.latest_session_updated_at)) {
        task.latest_session_updated_at = session.last_updated_at
        task.latest_session_state = session.state
      }
    } else {
      resourceIdToTaskMap.set(session.resource_id.toString(), {
        resource_type: session.resource_type,
        resource_id: session.resource_id,
        session_count: 1,
        latest_session_updated_at: session.last_updated_at,
        latest_session_state: session.state,
        event_identifiers: session.event_identifiers,
      })
    }
  }

  return Array.from(resourceIdToTaskMap.values()).sort((a, b) => {
    return new Date(b.latest_session_updated_at).getTime() - new Date(a.latest_session_updated_at).getTime()
  })
}

function hydrateTasks(tasks: Task[], idToPullRequestMap: IdToPullRequestMap): HydratedTask[] {
  const hydratedTasks: HydratedTask[] = []
  if (tasks && idToPullRequestMap) {
    for (const task of tasks) {
      const pull = idToPullRequestMap[task.resource_id]
      if (!pull) continue
      hydratedTasks.push({
        pull,
        ...task,
      })
    }
  }
  return hydratedTasks
}

export function useTasks({
  apiURL,
  sessionsPollingInterval = 3000,
  onCreateError = {},
  initialTasks,
  initialPullRequestsMap,
}: {
  apiURL: string
  sessionsPollingInterval?: number | false
  initialTasks?: Task[]
  initialPullRequestsMap?: IdToPullRequestMap
  onCreateError?: {
    onError?: (error: Error, variables: CreateTaskParams) => void
  }
}) {
  const isBranchSelectionEnabled = isFeatureEnabled('copilot_immersive_agent_branch_selection')
  const isCreateSessionViaFrontendEnabled = isFeatureEnabled('cca_create_session_from_front_end')

  const [submittedNewTasks, setSubmittedNewTasks] = useState<NewTaskPayload[]>([])

  const createTaskMutation = useMutation<undefined, Error, CreateTaskParams>({
    mutationKey: ['createTask'],
    mutationFn: async (params: CreateTaskParams) => {
      if (isCreateSessionViaFrontendEnabled) {
        await createJob({
          ...params,
          apiURL,
          isBranchSelectionEnabled,
        })
      } else {
        // Fall back to the monolith approach
        await createTaskViaMonolith({
          ...params,
          isBranchSelectionEnabled,
        })
      }
    },
    onError: (error, variables) => {
      // When a mutation fails, remove the corresponding task from the optimistic list.
      // In the future we want to keep the rows in the list and put a direct retry button inline
      setSubmittedNewTasks(prev => prev.filter(task => task.id !== variables.id))

      // Call the passed callback with returned error and original textarea input variables
      onCreateError?.onError?.(error, variables)
    },
  })

  const query = useCAPIQuery<Task[]>({
    queryKey: ['agent-tasks', apiURL],
    queryFn: async () => {
      const response = await fetchCAPI({
        apiURL,
        path: '/agents/sessions',
      })
      const data = await response.json()
      const sessions = data.sessions

      return sortTasks(sessions as Session[])
    },
    initialData: initialTasks,
    refetchInterval: sessionsPollingInterval,
    refetchIntervalInBackground: false,
  })

  // Then use those tasks to get pull requests
  const {
    data: idToPullRequestMap,
    isLoading: isLoadingPullRequests,
    isError: isErrorPullRequests,
  } = usePullRequests({
    tasks: query.data || [],
    sessionsPollingInterval,
    initialPullRequestsMap,
  })

  const hydratedServerTasks = useMemo(() => {
    return query.data && idToPullRequestMap ? hydrateTasks(query.data, idToPullRequestMap) : []
  }, [query.data, idToPullRequestMap])

  // Filter the pending tasks to remove any that have been successfully created.
  const pendingTasks = submittedNewTasks.filter((payload: NewTaskPayload) => {
    return !hydratedServerTasks.some(hydratedTask => {
      return hydratedTask.event_identifiers?.includes(payload.id)
    })
  })

  const createTask = (params: CreateTaskParams) => {
    const newCreatedTask: NewTaskPayload = {...params, submittedAt: Date.now()}
    // We create a bucket of all created tasks by the user during this component mounted lifecycle
    setSubmittedNewTasks(prev => [newCreatedTask, ...prev])

    // Start the mutation
    createTaskMutation.mutate(params)
  }

  // isLoading should only represent the initial fetch of tasks and associated pull requests (and should never be true when we have initial SSR data).
  // If we leave off the `idToPullRequestMap` check, isLoading will turn true whenever the tasks change (e.g. when a new task is created),
  // because it creates a brand new usePullRequest query key and isLoadingPullRequests will become true.
  // This is not what we want, as we want to show the loading state only when the initial fetch is happening and all further updates are reactively handled.
  // So we check if `idToPullRequestMap` has any data, and if it does, we don't consider it loading.
  // If we leave off isLoadingPullRequests entirely, we see an issue in the UI where on the first load (if there's no initial data), loading finishes too early
  // and there's simply no hydratedServerTasks yet, so we see an unwanted empty state for a brief moment.
  // There's lots of room for simplifying how we architect this, but for now this is the best we can do.
  const isLoading = query.isLoading || (isLoadingPullRequests && Object.keys(idToPullRequestMap).length === 0)

  const isError = query.isError || isErrorPullRequests

  return {
    tasks: hydratedServerTasks,
    pendingTasks,
    isLoading,
    isError,
    createSession: createTask,
  }
}
